/**
 * MA-ARCH-181: Attribution Coverage Summary
 * 
 * Compact reusable coverage bar for LP Performance, PP Detail, and War Room.
 * Shows deterministic/inferred/unknown split with percentages.
 * Includes weak-coverage guidance nudge when High Confidence < 40%.
 * 
 * Props:
 *   profitPathId           — filter by PP
 *   landingPageVersionId   — filter by LP version
 *   dateFrom / dateTo      — date range filter
 *   compact                — smaller layout
 */

import { useQuery } from "@tanstack/react-query";
import { ShieldCheck, ShieldAlert, ShieldQuestion, Info } from "lucide-react";
import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from "@/components/ui/tooltip";
import { api } from "@/lib/api";
import { cn } from "@/lib/utils";

const WEAK_COVERAGE_THRESHOLD = 40; // Show nudge when High Confidence < 40%

function useCoverage({ profitPathId, landingPageVersionId, dateFrom, dateTo }) {
  return useQuery({
    queryKey: ["attribution-coverage", profitPathId, landingPageVersionId, dateFrom, dateTo],
    queryFn: async () => {
      const params = new URLSearchParams();
      if (profitPathId) params.append("profit_path_id", profitPathId);
      if (landingPageVersionId) params.append("landing_page_version_id", landingPageVersionId);
      if (dateFrom) params.append("date_from", dateFrom);
      if (dateTo) params.append("date_to", dateTo);
      
      try {
        const res = await api.get(`/attribution/coverage?${params}`);
        return res.data;
      } catch (error) {
        // MA-PHASE11A-FINAL-APPROVAL-VALIDATION-GATE-023: Silent fail if permission denied
        if (error.response?.status === 403 || error.response?.status === 404) {
          return null;
        }
        throw error;
      }
    },
    staleTime: 60000,
    retry: false,
  });
}

function CoverageBar({ deterministic, inferred, unknown, total }) {
  if (total === 0) return null;
  const dPct = (deterministic / total) * 100;
  const iPct = (inferred / total) * 100;
  const uPct = (unknown / total) * 100;

  return (
    <div className="w-full h-2 rounded-full bg-slate-800 overflow-hidden flex" data-testid="attribution-coverage-bar">
      {dPct > 0 && (
        <div
          className="h-full bg-emerald-500 transition-all"
          style={{ width: `${dPct}%` }}
        />
      )}
      {iPct > 0 && (
        <div
          className="h-full bg-amber-500 transition-all"
          style={{ width: `${iPct}%` }}
        />
      )}
      {uPct > 0 && (
        <div
          className="h-full bg-slate-600 transition-all"
          style={{ width: `${uPct}%` }}
        />
      )}
    </div>
  );
}

function CoverageLegendItem({ icon: Icon, label, count, pct, colorClass }) {
  return (
    <div className="flex items-center gap-1.5">
      <Icon className={cn("h-3 w-3", colorClass)} />
      <span className="text-xs text-slate-400">{label}</span>
      <span className="text-xs font-medium text-slate-200">{pct}%</span>
      <span className="text-[10px] text-slate-600">({count})</span>
    </div>
  );
}

export default function AttributionCoverageSummary({
  profitPathId,
  landingPageVersionId,
  dateFrom,
  dateTo,
  compact = false,
}) {
  const { data, isLoading } = useCoverage({
    profitPathId,
    landingPageVersionId,
    dateFrom,
    dateTo,
  });

  if (isLoading) {
    return (
      <div className="animate-pulse flex gap-2 items-center py-2" data-testid="attribution-coverage-loading">
        <div className="h-2 w-full max-w-xs bg-slate-800 rounded-full" />
      </div>
    );
  }

  if (!data || data.total === 0) {
    return (
      <div className="text-xs text-slate-500 py-1" data-testid="attribution-coverage-empty">
        No attribution data available
      </div>
    );
  }

  const isWeak = data.deterministic_pct < WEAK_COVERAGE_THRESHOLD;
  const scopeLabel = data.scope === "profit_path"
    ? "this Profit Path"
    : data.scope === "landing_page"
    ? "this Landing Page"
    : "all leads";

  return (
    <div className={cn("space-y-2", compact ? "py-1" : "py-2")} data-testid="attribution-coverage-summary">
      {!compact && (
        <div className="flex items-center gap-1.5">
          <span className="text-xs font-medium text-slate-300">Attribution Coverage</span>
          <TooltipProvider>
            <Tooltip>
              <TooltipTrigger asChild>
                <Info className="h-3 w-3 text-slate-500 cursor-help" />
              </TooltipTrigger>
              <TooltipContent side="top" className="bg-slate-800 border-slate-700 max-w-xs">
                <p className="text-xs">
                  Shows how confidently leads are attributed for {scopeLabel}.
                  High Confidence means reliable, verifiable lineage.
                </p>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        </div>
      )}

      <CoverageBar
        deterministic={data.deterministic_count}
        inferred={data.inferred_count}
        unknown={data.unknown_count}
        total={data.total}
      />

      <div className={cn("flex flex-wrap gap-x-4 gap-y-1", compact ? "gap-x-3" : "")}>
        <CoverageLegendItem
          icon={ShieldCheck}
          label="High"
          count={data.deterministic_count}
          pct={data.deterministic_pct}
          colorClass="text-emerald-400"
        />
        <CoverageLegendItem
          icon={ShieldAlert}
          label="Medium"
          count={data.inferred_count}
          pct={data.inferred_pct}
          colorClass="text-amber-400"
        />
        <CoverageLegendItem
          icon={ShieldQuestion}
          label="Unknown"
          count={data.unknown_count}
          pct={data.unknown_pct}
          colorClass="text-slate-500"
        />
      </div>

      {/* Guidance nudge — only when coverage is weak and surface is attribution-sensitive */}
      {isWeak && data.total >= 5 && (
        <div
          className="flex items-start gap-2 px-3 py-2 rounded-lg bg-amber-500/8 border border-amber-500/15 mt-1"
          data-testid="attribution-coverage-nudge"
        >
          <Info className="h-3.5 w-3.5 text-amber-400 mt-0.5 flex-shrink-0" />
          <p className="text-xs text-amber-300/80 leading-relaxed">
            {data.deterministic_pct < 10
              ? "Most leads are missing high-confidence attribution. Use MissionAssist landing pages and connected funnel flows for the strongest attribution."
              : "Some leads are missing high-confidence attribution. To improve coverage, route more traffic through MissionAssist landing pages."}
          </p>
        </div>
      )}
    </div>
  );
}

export { useCoverage, WEAK_COVERAGE_THRESHOLD };
